<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Blueprints</h1>
    <p class="!mt-0 text-muted-color">
      Manage reusable quote structure templates.
    </p>
  </div>
  <p-button
    label="New Blueprint"
    icon="pi pi-plus"
    (onClick)="onCreateBlueprint()"
  />
</div>

@if (allBlueprints.length > 0) {
  <div
    class="rounded-xl border border-surface-switch-200 shadow-sm overflow-x-auto"
  >
    <!-- Header Row -->
    <div
      class="hidden lg:grid rounded-t-xl bg-surface-switch-100 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase border-b border-surface-switch-200"
      style="
        grid-template-columns: 2rem 2fr 1.5fr 1.5fr 1.5fr 1fr 1fr;
        min-width: 42rem;
      "
    >
      <div></div>
      <div>Name</div>
      <div>Created</div>
      <div>Created By</div>
      <div>Default Material</div>
      <div>Items</div>
      <div class="text-center">Actions</div>
    </div>

    <div class="flex flex-col">
      @for (blueprint of allBlueprints; track blueprint.id) {
        <div
          class="border-b border-surface-switch-200 last:border-b-0 transition-colors duration-200"
          [class.bg-surface-switch-100]="expandedRows.has(blueprint.id)"
          [class.bg-surface-switch-50]="!expandedRows.has(blueprint.id)"
          [class.hover:bg-surface-switch-100]="!expandedRows.has(blueprint.id)"
        >
          <!-- Mobile Layout -->
          <div class="lg:hidden p-4 flex flex-col gap-2">
            <div class="flex justify-between items-start">
              <div>
                <span class="font-semibold text-lg">{{ blueprint.name }}</span>
                @if (blueprint.creator) {
                  <span class="ml-2 text-sm text-muted-color"
                    >by {{ blueprint.creator }}</span
                  >
                } @else if (blueprint.preset) {
                  <span class="ml-2 text-sm text-muted-color"
                    >Preset Blueprint</span
                  >
                } @else {
                  <span class="ml-2 text-sm text-muted-color">—</span>
                }
              </div>
              <div class="flex gap-1 shrink-0 ml-2">
                <p-button
                  [text]="true"
                  severity="secondary"
                  [rounded]="true"
                  size="small"
                  [icon]="
                    expandedRows.has(blueprint.id)
                      ? 'pi pi-chevron-down'
                      : 'pi pi-chevron-right'
                  "
                  (onClick)="toggleRow(blueprint.id)"
                />
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="onEditBlueprint(blueprint)"
                  pTooltip="Edit"
                  tooltipPosition="top"
                />
                <p-button
                  icon="pi pi-trash"
                  severity="danger"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="onDeleteBlueprint($event, blueprint)"
                  pTooltip="Delete"
                  tooltipPosition="top"
                />
              </div>
            </div>
            <div class="text-sm text-muted-color">
              {{ blueprint.created_date | date: "mediumDate" }} ·
              {{ blueprint.items?.length ?? 0 }} item{{
                blueprint.items?.length === 1 ? "" : "s"
              }}
            </div>
          </div>

          <!-- Desktop Layout -->
          <div
            class="hidden lg:grid lg:items-center lg:gap-4 lg:px-4 lg:py-3"
            style="
              grid-template-columns: 2rem 2fr 1.5fr 1.5fr 1.5fr 1fr 1fr;
              min-width: 42rem;
            "
          >
            <div>
              <p-button
                [text]="true"
                severity="secondary"
                [rounded]="true"
                size="small"
                [icon]="
                  expandedRows.has(blueprint.id)
                    ? 'pi pi-chevron-down'
                    : 'pi pi-chevron-right'
                "
                (onClick)="toggleRow(blueprint.id)"
              />
            </div>
            <div class="font-semibold text-sm">{{ blueprint.name }}</div>
            <div class="text-sm text-muted-color">
              {{ blueprint.created_date | date: "mediumDate" }}
            </div>
            <div class="text-sm">
              {{
                blueprint.creator ||
                  (blueprint.preset ? "Preset Blueprint" : "—")
              }}
            </div>
            <div class="text-sm">
              @if (blueprint.default_material_name) {
                <span
                  class="px-2 py-0.5 bg-primary/20 text-primary rounded text-xs"
                  >{{ blueprint.default_material_name }}</span
                >
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm tabular-nums">
              {{ blueprint.items?.length ?? 0 }}
            </div>
            <div class="flex justify-center gap-1">
              <p-button
                icon="pi pi-pencil"
                severity="secondary"
                size="small"
                [text]="true"
                [rounded]="true"
                (onClick)="onEditBlueprint(blueprint)"
                pTooltip="Edit"
                tooltipPosition="top"
              />
              <p-button
                icon="pi pi-trash"
                severity="danger"
                size="small"
                [text]="true"
                [rounded]="true"
                (onClick)="onDeleteBlueprint($event, blueprint)"
                pTooltip="Delete"
                tooltipPosition="top"
              />
            </div>
          </div>

          <!-- Expanded Items -->
          @if (expandedRows.has(blueprint.id)) {
            <div
              class="px-6 py-4 border-t border-surface-switch-200 bg-surface-switch-100"
            >
              @let rootItems = getRootItems(blueprint);
              @if (rootItems.length > 0) {
                <div class="space-y-2">
                  @for (item of rootItems; track item.id) {
                    <app-blueprint-item-viewer
                      [blueprintItem]="item"
                      [blueprint]="blueprint"
                      [blueprintMap]="blueprintMap"
                    />
                  }
                </div>
              } @else {
                <p class="text-sm text-muted-color italic">(Empty blueprint)</p>
              }
            </div>
          }
        </div>
      }
    </div>
  </div>
} @else {
  <div class="text-center text-muted-color py-12">
    <p>No blueprints found</p>
  </div>
}

<app-blueprint-editor
  [(visible)]="showBlueprintEditor"
  [blueprint]="editingBlueprint"
  [allBlueprints]="allBlueprints"
  (submitBlueprint)="onSubmitBlueprint($event)"
/>

<shared-confirm-dialog />
